import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Loader2, Globe, Copy, Check } from "lucide-react";

import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogFooter,
} from "../ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";

import api from "../../services/api";
import toastError from "../../errors/toastError";

// O widget de site. Diferente dos outros canais, não há conta de terceiro
// para autorizar: o que se configura aqui é a aparência, o que o visitante
// precisa informar antes de falar e de quais sites o chat pode abrir.
//
// O mesmo modal cria e edita. Editar importa mais aqui do que nos outros
// canais — a cor e o texto do widget acompanham o site do cliente, e ele
// muda.

const PADRAO = {
	titulo: "Fale com a gente",
	subtitulo: "Respondemos por aqui mesmo",
	cor: "#25D366",
	saudacao: "Olá! Como podemos ajudar?",
	pedirNome: true,
	pedirEmail: false,
	pedirTelefone: false,
	mensagemOffline: "",
	dominios: "",
};

const WebchatConnectModal = ({ open, onClose, whatsAppId }) => {
	const editando = Boolean(whatsAppId);

	const [form, setForm] = useState(PADRAO);
	const [name, setName] = useState("");
	const [token, setToken] = useState("");
	const [salvando, setSalvando] = useState(false);
	const [copiado, setCopiado] = useState(false);
	const [aba, setAba] = useState("aparencia");

	useEffect(() => {
		if (!open) return;

		if (!editando) {
			setForm(PADRAO);
			setName("");
			setToken("");
			setAba("aparencia");
			return;
		}

		let ativo = true;

		(async () => {
			try {
				const { data } = await api.get(`/whatsapp/${whatsAppId}`);

				if (!ativo) return;

				const config = data.webchatConfig || {};

				setName(data.name || "");
				setToken(data.webchatToken || "");
				setForm({
					...PADRAO,
					...config,
					// Os domínios são uma lista no banco e uma linha por domínio na
					// tela: pedir JSON a quem só quer autorizar o próprio site seria
					// um convite a errar.
					dominios: (config.dominios || []).join("\n"),
				});
			} catch (err) {
				toastError(err);
			}
		})();

		return () => {
			ativo = false;
		};
	}, [open, whatsAppId, editando]);

	const fechar = useCallback(() => {
		setSalvando(false);
		setCopiado(false);
		onClose();
	}, [onClose]);

	const mudar = (campo) => (valor) => setForm((p) => ({ ...p, [campo]: valor }));

	const salvar = async (e) => {
		e.preventDefault();
		setSalvando(true);

		const config = {
			...form,
			dominios: form.dominios
				.split("\n")
				.map((d) => d.trim())
				.filter(Boolean),
		};

		try {
			if (editando) {
				await api.put(`/whatsapp/${whatsAppId}`, {
					name: name.trim() || "Chat do site",
					webchatConfig: config,
				});

				toast.success("Widget atualizado.");
				fechar();
				return;
			}

			const { data } = await api.post("/whatsapp/webchat", {
				name: name.trim() || undefined,
				...config,
			});

			setToken(data.webchatToken || "");
			// Não fecha: o token só existe agora, e é a única coisa que a pessoa
			// veio buscar. Fechar deixaria ela procurando onde copiar o script.
			setAba("instalar");
			toast.success("Widget criado. Copie o script e cole no seu site.");
		} catch (err) {
			toastError(err);
		} finally {
			setSalvando(false);
		}
	};

	const base = (import.meta.env.VITE_BACKEND_URL || "").replace(/\/$/, "");
	const snippet = `<script src="${base}/webchat.js" data-webchat-token="${token}" async></script>`;

	const copiar = async () => {
		try {
			await navigator.clipboard.writeText(snippet);
			setCopiado(true);
			setTimeout(() => setCopiado(false), 2000);
		} catch {
			toast.error("Não foi possível copiar. Selecione o texto e copie à mão.");
		}
	};

	return (
		<Dialog open={open} onOpenChange={(o) => !o && fechar()}>
			<DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
				<DialogHeader>
					<DialogTitle className="flex items-center gap-2">
						<Globe className="h-5 w-5 text-indigo-500" />
						{editando ? "Chat do site" : "Criar chat do site"}
					</DialogTitle>
					<DialogDescription>
						Uma bolha de conversa no seu site. Quem escrever por ela abre um
						atendimento aqui, com fila, chatbot e fluxo como qualquer conexão.
					</DialogDescription>
				</DialogHeader>

				<Tabs value={aba} onValueChange={setAba}>
					<TabsList className="w-full">
						<TabsTrigger value="aparencia" className="flex-1">
							Aparência
						</TabsTrigger>
						<TabsTrigger value="dados" className="flex-1">
							Dados do visitante
						</TabsTrigger>
						<TabsTrigger value="instalar" className="flex-1" disabled={!token}>
							Instalar
						</TabsTrigger>
					</TabsList>

					<form onSubmit={salvar}>
						<TabsContent value="aparencia" className="space-y-4 pt-4">
							<div className="space-y-1.5">
								<Label htmlFor="wc-nome">Nome da conexão</Label>
								<Input
									id="wc-nome"
									value={name}
									onChange={(ev) => setName(ev.target.value)}
									placeholder="Chat do site"
								/>
								<p className="text-xs text-muted-foreground">
									Só aparece aqui dentro, na lista de conexões e no atendimento.
								</p>
							</div>

							<div className="space-y-1.5">
								<Label htmlFor="wc-titulo">Título do widget</Label>
								<Input
									id="wc-titulo"
									value={form.titulo}
									onChange={(ev) => mudar("titulo")(ev.target.value)}
								/>
							</div>

							<div className="space-y-1.5">
								<Label htmlFor="wc-subtitulo">Subtítulo</Label>
								<Input
									id="wc-subtitulo"
									value={form.subtitulo}
									onChange={(ev) => mudar("subtitulo")(ev.target.value)}
								/>
							</div>

							<div className="space-y-1.5">
								<Label htmlFor="wc-cor">Cor</Label>
								<div className="flex items-center gap-2">
									<input
										id="wc-cor"
										type="color"
										value={form.cor}
										onChange={(ev) => mudar("cor")(ev.target.value)}
										className="h-9 w-14 cursor-pointer rounded border bg-transparent"
									/>
									<Input
										value={form.cor}
										onChange={(ev) => mudar("cor")(ev.target.value)}
										className="font-mono"
									/>
								</div>
								<p className="text-xs text-muted-foreground">
									Vale para a bolha, o cabeçalho e as mensagens do visitante.
								</p>
							</div>

							<div className="space-y-1.5">
								<Label htmlFor="wc-saudacao">Saudação</Label>
								<Textarea
									id="wc-saudacao"
									rows={2}
									value={form.saudacao}
									onChange={(ev) => mudar("saudacao")(ev.target.value)}
								/>
								<p className="text-xs text-muted-foreground">
									Primeira frase da conversa. Aparece só na tela do visitante —
									não cria atendimento.
								</p>
							</div>
						</TabsContent>

						<TabsContent value="dados" className="space-y-4 pt-4">
							<p className="text-sm leading-relaxed text-muted-foreground">
								O que perguntar antes de a conversa começar. Cada campo é uma
								barreira a mais entre a pessoa e o atendimento — com nenhum
								marcado, o chat abre direto.
							</p>

							{[
								["pedirNome", "Pedir o nome"],
								["pedirEmail", "Pedir o e-mail"],
								["pedirTelefone", "Pedir o telefone"],
							].map(([campo, rotulo]) => (
								<div key={campo} className="flex items-center justify-between">
									<Label htmlFor={`wc-${campo}`}>{rotulo}</Label>
									<Switch
										id={`wc-${campo}`}
										checked={Boolean(form[campo])}
										onCheckedChange={mudar(campo)}
									/>
								</div>
							))}

							<div className="space-y-1.5 border-t pt-4">
								<Label htmlFor="wc-offline">Mensagem fora do horário</Label>
								<Textarea
									id="wc-offline"
									rows={2}
									value={form.mensagemOffline}
									onChange={(ev) => mudar("mensagemOffline")(ev.target.value)}
									placeholder="Em branco, usa o aviso de fora de horário da conexão."
								/>
								<p className="text-xs text-muted-foreground">
									Aparece no topo do widget quando a empresa está fechada, pelo
									horário configurado na aba de horários desta conexão.
								</p>
							</div>

							<div className="space-y-1.5">
								<Label htmlFor="wc-dominios">Domínios autorizados</Label>
								<Textarea
									id="wc-dominios"
									rows={3}
									value={form.dominios}
									onChange={(ev) => mudar("dominios")(ev.target.value)}
									placeholder={"exemplo.com.br\nloja.exemplo.com.br"}
									className="font-mono text-sm"
								/>
								<p className="text-xs text-muted-foreground">
									Um por linha. Em branco, o widget abre em qualquer site que
									tenha o script — inclusive no de outra pessoa. Escrever
									<code className="mx-1">exemplo.com</code> já cobre os
									subdomínios.
								</p>
							</div>
						</TabsContent>

						<TabsContent value="instalar" className="space-y-4 pt-4">
							<p className="text-sm leading-relaxed text-muted-foreground">
								Cole esta linha antes do <code>&lt;/body&gt;</code> do seu site.
								É só isso — o widget se desenha sozinho.
							</p>

							<div className="rounded-lg border bg-muted/40 p-3">
								<code className="block break-all font-mono text-xs leading-relaxed">
									{snippet}
								</code>
							</div>

							<Button
								type="button"
								variant="outline"
								className="w-full"
								onClick={copiar}
							>
								{copiado ? (
									<Check className="h-4 w-4 text-emerald-500" />
								) : (
									<Copy className="h-4 w-4" />
								)}
								{copiado ? "Copiado" : "Copiar script"}
							</Button>

							<div className="rounded-lg border bg-muted/40 p-3 text-xs leading-relaxed text-muted-foreground">
								O token no script identifica este widget. Quem o tiver consegue
								abrir conversas em nome da sua empresa — por isso vale preencher
								os domínios autorizados na aba anterior.
							</div>
						</TabsContent>

						<DialogFooter className="pt-4">
							<Button type="button" variant="outline" onClick={fechar}>
								{token && !editando ? "Fechar" : "Cancelar"}
							</Button>
							<Button type="submit" disabled={salvando}>
								{salvando && <Loader2 className="h-4 w-4 animate-spin" />}
								{editando ? "Salvar" : "Criar widget"}
							</Button>
						</DialogFooter>
					</form>
				</Tabs>
			</DialogContent>
		</Dialog>
	);
};

export default WebchatConnectModal;
